import React from 'react'

import { nanoid } from 'nanoid'
import { BaseLocationHook, Router } from 'wouter'
import { useBrowserLocation } from 'wouter/use-browser-location'

import { WallabyRouterContext } from './contexts'
import {
  BeforeRouteChangeHandler,
  RouteChangeArgs,
  RouteChangeHandler,
} from './types'

// this hook is ONLY used by the router itself, not exported.
const useWallabyLocationHook: BaseLocationHook = () => {
  const ctx = React.useContext(WallabyRouterContext)
  const [currentLocation, setLocation] = useBrowserLocation()

  const handleLocationChange = async (
    location: string,
    options?: { replace?: boolean },
  ) => {
    // We only want to compare the path, we don't care about the query params
    // (like debug flags)
    const [nextRoutePath] = location.split('?')
    const pathChanged = currentLocation !== nextRoutePath

    if (!pathChanged) {
      setLocation(location, options)
      return
    }

    const args = { from: currentLocation, to: nextRoutePath }
    const { cancel } = await ctx.checkUnload(args)
    if (!cancel) {
      ctx.beforeExit(args)
      setLocation(location, options)
    }
  }

  return [currentLocation, handleLocationChange]
}

export const WallabyRouter = ({ children }: { children: React.ReactNode }) => {
  // registry of current unload handlers
  const { current: beforeUnloadHandlers } = React.useRef<{
    [key: string]: BeforeRouteChangeHandler
  }>({})

  const { current: unloadHandlers } = React.useRef<{
    [key: string]: RouteChangeHandler
  }>({})

  const registerUnloadHandler = (value: RouteChangeHandler) => {
    const id = nanoid()
    unloadHandlers[id] = value
    return () => delete unloadHandlers[id]
  }

  const registerBeforeUnloadHandler = (value: BeforeRouteChangeHandler) => {
    const id = nanoid()
    beforeUnloadHandlers[id] = value
    return () => delete beforeUnloadHandlers[id]
  }

  const beforeExit = (args: RouteChangeArgs) => {
    Object.values(unloadHandlers).forEach((fn) => {
      fn(args)
    })
  }

  const checkUnload = async (args: RouteChangeArgs) => {
    const checks = Object.values(beforeUnloadHandlers)
    // we iterate through each of the checks in random order. If any
    // one of them aborts, skip the rest and block the transition
    for (let i = 0; i < checks.length; i++) {
      const check = checks[i]
      if ((await check(args)).cancel) {
        return { cancel: true }
      }
    }
    return { cancel: false }
  }

  return (
    <WallabyRouterContext.Provider
      value={{
        registerUnloadHandler,
        registerBeforeUnloadHandler,
        checkUnload,
        beforeExit,
      }}
    >
      <Router hook={useWallabyLocationHook}>{children}</Router>
    </WallabyRouterContext.Provider>
  )
}
